import ListingTable from 'common/components/listingTable/ListingTable';
import ListingTableCache, { ListingTableData } from 'common/components/listingTable/ListingTableCache';
import { FilterData, ListingTableColumn, OrderData } from 'common/components/listingTable/models';
import ListingTableSidebar from 'common/components/listingTable/sidebar/ListingTableSidebar';
import { orderColumns } from 'common/components/listingTable/tableUtils';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaEdit, FaTrash } from 'react-icons/fa';
import { DATE_TIME_FORMAT_DEFAULT, DEFAULT_PAGINATION_ITEMS_PER_PAGE, LOGGER_LOG_TYPE } from 'Config';
import Loading from 'common/services/Loading';
import Logger from 'common/services/Logger';
import TableActionButton from 'common/components/listingTable/actionButton/TableActionButton';
import PageContainer from 'common/components/pageContainer/PageContainer';
import ScreenTitle from 'common/components/screenTitle/ScreenTitle';
import PageBreadcrumbsPortal from 'common/components/pageBreadcrumbsPortal/PageBreadcrumbsPortal';
import PageHeader from 'common/components/pageHeader/PageHeader';
import { useSelector } from 'react-redux';
import { Reducers } from 'store/types';
import { UserProfile } from 'api/account/models/UserProfile';
import UsersService from 'api/users/UsersService';
import Toast from 'common/services/Toast';
import DateFormat from 'common/components/dateFormat/dateFormat';
import { BudgetFieldDto } from 'api/budgetsFields/models/BudgetFieldDto';
import { BudgetsFieldsSearchCriteria } from 'api/budgetsFields/models/BudgetsFieldsSearchCriteria';
import { BudgetFieldType } from 'api/budgetsFields/enums/BudgetFieldType';
import BudgetsFieldsService from 'api/budgetsFields/BudgetsFieldsService';
import Button from 'common/components/button/Button';
import ConfirmDeleteModal from 'common/components/modal/confirmDeleteModal/ConfirmDeleteModal';

const PAGE_ID = 'BACKOFFICE_BUDGET_FIELDS';
const baseUrl = '/backoffice/budgets-fields';

function BudgetsFieldsList(): JSX.Element | null {
    const { t, i18n } = useTranslation();
    const [tableData, setTableData] = useState<ListingTableData<BudgetFieldDto, Partial<BudgetsFieldsSearchCriteria>>>(ListingTableCache.get(PAGE_ID, {
        order: { field: 'position', isOrderAsc: true },
        hiddenColumns: [],
        columnsOrder: [],
        columnsWidth: [],
        page: 1,
        filters: {},
        lastRowId: null,
    }));
    const [budgetFields, setBudgetsFields] = useState<BudgetFieldDto[]>([]);
    const [totalItems, setTotalItems] = useState<number>(0)
    const [refreshKey, setRefreshKey] = useState(0);
    const [budgetFieldToDelete, setBudgetFieldToDelete] = useState<BudgetFieldDto | null>(null);
    const [showConfirmDeleteModal, setShowConfirmDeleteModal] = useState(false);
    const [hasMore, setHasMore] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const loggedUser = useSelector<Reducers, UserProfile | null>(state => state.authentication.profile);
    const canWrite = UsersService.hasPolicies(loggedUser?.policies ?? [], ['BUDGETS_FIELDS_WRITE'])
    const canRead = UsersService.hasPolicies(loggedUser?.policies ?? [], ['BUDGETS_FIELDS_READ'])
    const [scrollToRowId, setScrollToRowId] = useState<string | null>(null);
    const firstLoad = useRef(true);
    const fieldTypesOptions = useMemo(() => Object.keys(BudgetFieldType).map(key => ({
        label: t('budgets_fields.field_types.' + key),
        value: key,
    })), [i18n.language]);

    const columns: ListingTableColumn<BudgetFieldDto>[] = orderColumns([
        {
            name: t('budgets_fields.list.position'),
            field: 'position',
            minWidth: '9rem',
        },
        {
            name: t('budgets_fields.list.name'),
            field: 'name',
            filter: { type: 'text', value: tableData.filters.name ?? '', placeholder: t('budgets_fields.list.name') },
            minWidth: '18rem',
        },
        {
            name: t('budgets_fields.list.field_type'),
            field: 'fieldType',
            orderColumn: 'field_type',
            filter: { type: 'dropdown', value: tableData.filters.fieldType ?? '', options: fieldTypesOptions || [] },
            minWidth: '18rem',
            renderCell: (row) => {
                return t('budgets_fields.field_types.' + row.fieldType);
            }
        },
        {
            name: t('common.updated_date'),
            field: 'updatedDate',
            filter: {
                type: 'date-range',
                value: {
                    start: tableData.filters.updatedDateStart ? new Date(tableData.filters.updatedDateStart) : null,
                    end: tableData.filters.updatedDateEnd ? new Date(tableData.filters.updatedDateEnd) : null
                }
            },
            minWidth: '22rem',
            orderColumn: 'updated_date',
            renderCell: (row) => {
                return row.updatedDate ? <DateFormat value={row.updatedDate} format={DATE_TIME_FORMAT_DEFAULT} /> : '';
            }
        },
        {
            name: t('common.user_updated'),
            field: 'userUpdatedName',
            filter: { type: 'text', value: tableData.filters.userUpdatedName ?? '', placeholder: t('common.user_updated') },
            minWidth: '21rem',
            orderColumn: 'user_updated_name',
        },
    ], tableData.columnsOrder);

    const renderAction = (row: BudgetFieldDto) => (
        <TableActionButton
            options={[
                { label: <span><FaEdit /> {t('common.edit')}</span>, href: `${baseUrl}/edit/${row.id}`, onClick: () => ListingTableCache.saveLastRowId(PAGE_ID, row.id) },
                { label: <span><FaTrash /> {t('common.delete')}</span>, onClick: () => onOpenModalToDelete(row) },
            ]}
        />
    )

    const onFilter = (col: ListingTableColumn<BudgetFieldDto>, filterData: FilterData) => {
        setTableData(d => {
            const data = ({
                ...d, page: 1
            })
            if (col.field === 'fieldType') {
                data.filters = {
                    ...data.filters,
                    fieldType: filterData.value,
                };
            }
            if (col.field === 'name') {
                data.filters = {
                    ...data.filters,
                    name: filterData.value,
                };
            }
            if (col.field === 'updatedDate') {
                data.filters = {
                    ...data.filters,
                    updatedDateStart: filterData.value?.start as Date,
                    updatedDateEnd: filterData.value?.end as Date
                };
            }

            if (col.field === 'userUpdatedName') {
                data.filters = {
                    ...data.filters,
                    userUpdatedName: filterData.value as string
                };
            }
            return data;
        });
        setRefreshKey(k => k + 1);
    }

    const onClearFilters = () => {
        setTableData(d => ({ ...d, page: 1, filters: {}, lastRowId: null }));
        setRefreshKey(k => k + 1);
    }

    const onOrder = (orderData: OrderData<BudgetFieldDto>) => {
        setTableData(d => ({ ...d, order: orderData, page: 1 }));
        setRefreshKey(k => k + 1);
    }

    useEffect(() => {
        void getData();
    }, [tableData.page, tableData.filters, tableData.order, refreshKey]);

    useEffect(() => {
        ListingTableCache.save(PAGE_ID, tableData);
    }, [tableData]);

    const getData = async () => {
        try {
            if (isLoading) {
                return;
            }

            Loading.show();
            setIsLoading(true);
            const data = await BudgetsFieldsService.getList({
                ...tableData.filters,
                page: firstLoad.current && tableData.page > 1 ? 1 : tableData.page,
                itemsPerPage: firstLoad.current && tableData.page > 1 ? DEFAULT_PAGINATION_ITEMS_PER_PAGE * tableData.page : DEFAULT_PAGINATION_ITEMS_PER_PAGE,
                orderColumn: tableData.order.orderColumn,
                orderBy: tableData.order.isOrderAsc ? 'asc' : 'desc'
            });

            const newListElements = data.currentPage === 1 ? data.items : [...budgetFields, ...data.items]
            setHasMore(data.totalItems > newListElements.length);
            setBudgetsFields(newListElements);
            setTotalItems(data.totalItems)

            if (firstLoad.current) {
                setScrollToRowId(tableData.lastRowId);
            }
            firstLoad.current = false;
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, 'Couldn\'t get budgets fields list', error);
            Toast.error(t('messages.error_load_info'));
        } finally {
            Loading.hide();
            setIsLoading(false);
        }
    }

    const onRowClick = (row: BudgetFieldDto) => {
        ListingTableCache.saveLastRowId(PAGE_ID, row.id);
    }

    const onLoadMore = () => {
        setTableData(d => ({ ...d, page: d.page + 1 }));
    }

    const onOpenModalToDelete = (row: BudgetFieldDto) => {
        setBudgetFieldToDelete(row);
        setShowConfirmDeleteModal(true)
    }

    const onDelete = async (result: boolean) => {
        if (!result) {
            setShowConfirmDeleteModal(false)
            return;
        }
        try {
            await BudgetsFieldsService.remove(budgetFieldToDelete as BudgetFieldDto)
            setTableData(d => ({ ...d, page: 1 }));
            setRefreshKey(k => k + 1);
            Loading.hide();
            Toast.success(t('messages.record_delete_success'));
        } catch (error: any) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, 'Couldn\'t delete budget field', error);
            Toast.error(t('messages.record_delete_error'));
        }
        setShowConfirmDeleteModal(false)
    }

    if (!canRead) {
        return null;
    }

    return (
        <ScreenTitle title={t('budgets_fields.list.title')}>
            <PageBreadcrumbsPortal
                breadcrumbs={[
                    { name: t('home.title'), url: '/' },
                    { name: t('budgets_fields.list.title'), url: '/backoffice/budgets-fields' },
                ]}
            />
            <PageHeader title={t('budgets_fields.list.title')} subTitle={`${totalItems} ${t('common.total_results')}`} informationText={t('common.list')} addSidebarSpacing>
                {canWrite && <Button href={`${baseUrl}/create`}>{t('common.new')}</Button>}
            </PageHeader>
            <PageContainer>
                <ListingTable<BudgetFieldDto>
                    columns={columns}
                    rows={budgetFields}
                    order={tableData.order}
                    hiddenColumns={tableData.hiddenColumns}
                    columnsWidths={tableData.columnsWidth}
                    onOrder={onOrder}
                    onFilter={onFilter}
                    onClearFilters={onClearFilters}
                    onRenderAction={canWrite ? renderAction : undefined}
                    onRowClick={onRowClick}
                    onHref={(row) => `${baseUrl}/details/${row.id}`}
                    infiniteScroll={{
                        isLoading,
                        hasMore,
                        onLoadMore,
                    }}
                    onRowId={r => r.id}
                    scrollToRowId={scrollToRowId}
                    onColResize={(w) => setTableData(d => ({ ...d, columnsWidth: w }))}
                />
            </PageContainer>
            <ListingTableSidebar
                columns={columns}
                hiddenColumns={tableData.hiddenColumns}
                onChangeHiddenColumns={(hc) => setTableData(d => ({ ...d, hiddenColumns: hc }))}
                onChangeColumnsOrder={(oc) => setTableData(d => ({ ...d, columnsOrder: oc }))}
            />
            <ConfirmDeleteModal
                itemName={budgetFieldToDelete?.name}
                isOpen={showConfirmDeleteModal}
                onClose={(result) => onDelete(result)}
            />
        </ScreenTitle>
    );
}

export default BudgetsFieldsList;
